import { Command as CommandRoot } from 'cmdk';
import { Input } from '@shelflix/ui-web/components/ui/input';
import { useEffect, useRef, useState } from 'react';
import { logger } from '@/utils/logger';

interface DropdownAction {
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
}

interface GenericSelectProps<T> {
  value?: (T & { disabled?: boolean }) | string | null;
  onSelect: (item: T & { disabled?: boolean }) => void;
  fetchItems: (search: string) => Promise<(T & { disabled?: boolean })[]>;
  placeholder?: string;
  renderItem: (item: T & { disabled?: boolean }) => React.ReactNode;
  getItemLabel: (item: T & { disabled?: boolean }) => string;
  getItemKey?: (item: T & { disabled?: boolean }) => string | number;
  disabled?: boolean;
  listOnEmptySearch?: boolean;
  onSearchChange?: (search: string) => void;
  renderSelectedItem?: (item: T & { disabled?: boolean }) => React.ReactNode;
  loading?: boolean;
  dependencies?: unknown[];
  /** Optional action to show at the top of the dropdown list */
  headerAction?: DropdownAction;
  /** Optional action pinned to the bottom of the open dropdown */
  footerAction?: DropdownAction;
}

export function GenericSelect<T>({
  value,
  onSelect,
  fetchItems,
  placeholder = 'Digite para buscar',
  renderItem,
  getItemLabel,
  getItemKey,
  disabled = false,
  listOnEmptySearch = true,
  onSearchChange,
  renderSelectedItem,
  loading = false,
  dependencies = [],
  headerAction,
  footerAction,
}: GenericSelectProps<T & { disabled?: boolean }>) {
  const [inputValue, setInputValue] = useState('');
  const [items, setItems] = useState<(T & { disabled?: boolean })[]>([]);
  const [showList, setShowList] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasFetchedInitial, setHasFetchedInitial] = useState(false);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const fetchItemsRef = useRef(fetchItems);

  // Update ref whenever fetchItems changes
  useEffect(() => {
    fetchItemsRef.current = fetchItems;
  }, [fetchItems]);

  // Sync inputValue only when there's a selected value AND we're not showing the list
  useEffect(() => {
    if (!showList && value && typeof value === 'object') {
      setInputValue(getItemLabel(value));
    }
  }, [value, getItemLabel, showList]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setShowList(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    // Skip fetching if component is disabled
    if (disabled) {
      setItems([]);
      setIsLoading(false);
      return;
    }

    if (!inputValue && !listOnEmptySearch) {
      setItems([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    logger.debug('📋 GenericSelect: starting fetch with inputValue:', inputValue);

    // Use shorter delay for initial fetch (when dropdown just opened)
    // and normal debounce for user typing
    const delayMs = !hasFetchedInitial && !inputValue ? 0 : 300;

    const timeout = setTimeout(() => {
      fetchItemsRef
        .current(inputValue)
        .then((data) => {
          logger.info('📋 GenericSelect: items received:', data);
          // Ensure data is always an array
          const itemsArray = Array.isArray(data) ? data : [];
          logger.info('📋 GenericSelect: items count:', itemsArray.length);
          setItems(itemsArray);
          setHasFetchedInitial(true);
        })
        .catch((error) => {
          logger.error('📋 GenericSelect: error fetching items:', error);
          setItems([]);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }, delayMs);

    return () => {
      clearTimeout(timeout);
    };
  }, [inputValue, listOnEmptySearch, disabled, hasFetchedInitial, ...dependencies]);

  const handleSelect = (item: T & { disabled?: boolean }) => {
    if (item.disabled) return;
    onSelect(item);
    setInputValue(getItemLabel(item));
    setShowList(false);
    onSearchChange?.(getItemLabel(item));
  };

  return (
    <div ref={wrapperRef} className="relative w-full">
      {renderSelectedItem && typeof value === 'object' && value !== null ? (
        // Custom render for selected item
        <div
          className={`relative w-full cursor-pointer rounded border px-3 py-2 text-sm ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
          onClick={() => !disabled && setShowList(true)}
        >
          {renderSelectedItem(value)}
        </div>
      ) : (
        // Default input mode
        <Input
          placeholder={placeholder}
          value={inputValue}
          onChange={(e) => {
            const newValue = e.target.value;
            setInputValue(newValue);
            setShowList(true);
            onSearchChange?.(newValue);
          }}
          onFocus={() => !disabled && setShowList(true)}
          disabled={disabled}
        />
      )}
      {showList && !disabled && (
        <div className="bg-popover absolute z-50 mt-1 w-full rounded-md border shadow-md">
          <CommandRoot
            shouldFilter={false}
            data-slot="command"
            className="bg-popover text-popover-foreground flex h-full w-full flex-col overflow-hidden rounded-md"
          >
            <CommandRoot.List className="max-h-[300px] scroll-py-1 overflow-x-hidden overflow-y-auto">
              {/* Header Action */}
              {headerAction && (
                <CommandRoot.Item
                  onSelect={() => {
                    headerAction.onClick();
                    setShowList(false);
                  }}
                  className="text-primary hover:bg-accent data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground relative flex cursor-pointer items-center gap-2 border-b px-2 py-2 text-sm font-medium select-none"
                >
                  {headerAction.icon}
                  {headerAction.label}
                </CommandRoot.Item>
              )}
              {isLoading || loading ? (
                <CommandRoot.Empty className="py-6 text-center text-sm">Carregando...</CommandRoot.Empty>
              ) : items.length > 0 ? (
                items.map((item, i) => (
                  <CommandRoot.Item
                    key={getItemKey ? getItemKey(item) : i}
                    onSelect={() => handleSelect(item)}
                    className={
                      item.disabled
                        ? 'cursor-not-allowed opacity-50'
                        : 'data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground relative flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm select-none'
                    }
                  >
                    {renderItem(item)}
                  </CommandRoot.Item>
                ))
              ) : (
                <CommandRoot.Empty className="py-6 text-center text-sm">Nenhum item encontrado</CommandRoot.Empty>
              )}
            </CommandRoot.List>
            {footerAction && (
              <button
                type="button"
                onClick={() => {
                  footerAction.onClick();
                  setShowList(false);
                }}
                className="text-primary hover:bg-accent flex w-full cursor-pointer items-center gap-2 border-t px-2 py-2 text-sm font-medium"
              >
                {footerAction.icon}
                {footerAction.label}
              </button>
            )}
          </CommandRoot>
        </div>
      )}
    </div>
  );
}
